import { type FormEvent, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useMeterSearch } from '../hooks/useMeterSearch';

export function MeterInspectorPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') ?? '');
  const activeQuery = searchParams.get('q');
  const { data, isLoading, error } = useMeterSearch(activeQuery);
  const navigate = useNavigate();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;

    // Recognize WaterScope Plus links and navigate directly to the meter
    const wsMatch = trimmed.match(
      /^https?:\/\/plus\.waterscope\.us\/(?:portfolio|property)\/[\d/]*?(\d+)\/?$/,
    );
    if (wsMatch) {
      navigate(`/meter-inspector/${wsMatch[1]}`);
      return;
    }

    setSearchParams({ q: trimmed });
  };

  return (
    <div className='flex flex-col items-center gap-8'>
      <div
        className={`flex flex-col items-center gap-6 w-full transition-all duration-300 ${
          activeQuery ? 'pt-4' : 'justify-center min-h-[60vh]'
        }`}
      >
        {!activeQuery && (
          <div className='text-center space-y-2'>
            <h1 className='text-3xl font-bold'>Meter Inspector</h1>
            <p className='text-muted-foreground'>
              Search for a meter by ID, address, consumer, or other identifier
            </p>
          </div>
        )}
        <form onSubmit={handleSubmit} className='relative w-full max-w-xl'>
          <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground' />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Search meters...'
            className='pl-10 pr-20 h-12 text-lg'
            autoFocus
          />
          <Button
            type='submit'
            size='sm'
            className='absolute right-1.5 top-1/2 -translate-y-1/2'
          >
            Search
          </Button>
        </form>
      </div>

      {activeQuery && (
        <div className='w-full max-w-3xl'>
          {isLoading && (
            <div className='flex items-center justify-center py-12'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
              <span className='ml-2 text-muted-foreground'>Searching...</span>
            </div>
          )}

          {error && (
            <div className='text-center py-12 text-destructive'>
              <p>Failed to search meters. Please try again.</p>
            </div>
          )}

          {data && data.meters.length === 0 && (
            <div className='text-center py-12 text-muted-foreground'>
              <p>No meters found for "{activeQuery}"</p>
            </div>
          )}

          {data && data.meters.length > 0 && (
            <div className='space-y-2'>
              <p className='text-sm text-muted-foreground mb-4'>
                {data.meters.length} result{data.meters.length !== 1 ? 's' : ''}
              </p>
              {data.meters.map((meter) => (
                <Link
                  key={meter.sensorId}
                  to={`/meter-inspector/${meter.sensorId}`}
                  className='block'
                >
                  <Card className='hover:bg-accent/50 transition-colors cursor-pointer'>
                    <CardContent className='py-3 px-4'>
                      <div className='flex items-center justify-between'>
                        <div className='space-y-1 min-w-0'>
                          <div className='flex items-center gap-3'>
                            <span className='font-mono font-medium'>
                              {meter.deviceId}
                            </span>
                            {meter.consumerId && (
                              <span className='text-xs text-muted-foreground'>
                                ID: {meter.consumerId}
                              </span>
                            )}
                          </div>
                          {meter.customerName && (
                            <div className='text-sm font-medium'>
                              {meter.customerName}
                            </div>
                          )}
                          <div className='flex items-center gap-3 text-sm text-muted-foreground'>
                            {meter.address && <span className='truncate'>{meter.address}</span>}
                            {meter.consumerName && (
                              <span className='truncate'>{meter.consumerName}</span>
                            )}
                          </div>
                        </div>
                        {meter.rate != null && (
                          <div className='text-right shrink-0 ml-4'>
                            <span className='text-sm font-medium'>
                              {Number(meter.rate).toFixed(2)}
                            </span>
                            <span className='text-xs text-muted-foreground ml-1'>
                              gal/min
                            </span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
